/**
 * @fileoverview Quota handling utilities for ExamFlip frontend
 * Handles quota exceeded errors and provides normalized quota information
 */

/**
 * @typedef {Object} QuotaInfo
 * @property {number} limit - The total limit for this quota scope
 * @property {number} used - The current usage amount
 * @property {number} remaining - The remaining quota amount
 * @property {string} [resetAt] - ISO date string when quota resets
 * @property {string} [scope] - The scope of the quota (e.g., 'AI', 'exam', 'questions')
 * @property {string} [endpoint] - The endpoint that triggered the quota error
 * @property {string} [message] - Human-readable error message
 * @property {number} [dailyLimit] - Daily limit if applicable
 * @property {number} [monthlyLimit] - Monthly limit if applicable
 * @property {number} [dailyUsage] - Current daily usage
 * @property {number} [monthlyUsage] - Current monthly usage
 * @property {string} [planName] - Name of the current plan
 * @property {string} [type] - Type of the quota (same as scope)
 */

/**
 * Custom error class for quota exceeded scenarios
 */
class QuotaExceededError extends Error {
  /**
   * @param {QuotaInfo} quota - Normalized quota information
   */
  constructor(quota) {
    const message = (quota && quota.message) || 'Quota exceeded';
    super(message);
    this.name = 'QuotaExceededError';
    this.quota = quota || {};
  }
}

/**
 * Check if an error is a quota exceeded error
 * @param {Error} error - Error to check
 * @returns {boolean} True if the error is a QuotaExceededError
 */
function isQuotaError(error) {
  return Boolean(error && error.name === 'QuotaExceededError');
}

/**
 * Normalize the quota payload from the backend API into a standardized format
 * Handles the RFC 9457 Problem Details format from the API
 * @param {Object} payload - Raw payload from 403 response
 * @returns {QuotaInfo} Normalized quota information
 */
function normalizeQuotaPayload(payload) {
  if (!payload || typeof payload !== 'object') {
    return {
      limit: 0,
      used: 0,
      remaining: 0,
      resetAt: undefined,
      scope: undefined,
      endpoint: undefined,
      message: 'Quota exceeded'
    };
  }

  // Handle RFC 9457 Problem Details format from API
  // Note: API uses capital 'UsageMetrics' not lowercase 'usageMetrics'
  const usageMetrics = payload.usageMetrics || payload.UsageMetrics || {};
  
  // Extract detail message with proper fallbacks
  let message = 'Quota exceeded';
  if (payload.detail && payload.detail !== 'Plan Limit Exceeded') {
    message = payload.detail;
  } else if (payload.message && payload.message !== 'Plan Limit Exceeded') {
    message = payload.message;
  } else if (payload.title && payload.title !== 'Plan Limit Exceeded') {
    message = payload.title;
  }

  // Extract values with fallbacks
  const limit = Number(usageMetrics.limit || payload.limit || 0);
  const dailyLimit = Number(usageMetrics.dailyLimit || payload.dailyLimit || 0);
  const monthlyLimit = Number(usageMetrics.monthlyLimit || payload.monthlyLimit || 0);
  
  const used = Number(usageMetrics.usage || payload.used || payload.current || 0);
  const dailyUsage = Number(usageMetrics.dailyUsage || payload.dailyUsage || 0);
  const monthlyUsage = Number(usageMetrics.monthlyUsage || payload.monthlyUsage || 0);

  // Calculate remaining based on the most restrictive limit that was exceeded
  let remaining = 0;
  let effectiveLimit = limit;
  let effectiveUsed = used;

  // Check if daily limit was the one exceeded
  if (dailyLimit > 0 && dailyUsage >= dailyLimit) {
    effectiveLimit = dailyLimit;
    effectiveUsed = dailyUsage;
    remaining = Math.max(0, dailyLimit - dailyUsage);
  }
  // Check if monthly limit was the one exceeded  
  else if (monthlyLimit > 0 && monthlyUsage >= monthlyLimit) {
    effectiveLimit = monthlyLimit;
    effectiveUsed = monthlyUsage;
    remaining = Math.max(0, monthlyLimit - monthlyUsage);
  }
  // Otherwise use total limit
  else if (limit > 0) {
    remaining = Math.max(0, limit - used);
  }
  // Fallback calculation
  else {
    remaining = Number(payload.remaining || 0);
  }

  return {
    limit: effectiveLimit,
    used: effectiveUsed,
    remaining: remaining,
    resetAt: usageMetrics.date || payload.resetAt || payload.reset_at || payload.reset || undefined,
    scope: usageMetrics.type || payload.scope || payload.type || undefined,
    endpoint: payload.endpoint || undefined,
    message: message,
    dailyLimit: dailyLimit || undefined,
    monthlyLimit: monthlyLimit || undefined,
    dailyUsage: dailyUsage || undefined,
    monthlyUsage: monthlyUsage || undefined,
    planName: usageMetrics.planName || payload.planName || undefined,
    type: usageMetrics.type || payload.type || undefined
  };
}

/**
 * Format reset time for display
 * @param {string} resetAt - ISO date string
 * @returns {string} Formatted date string
 */
function formatResetTime(resetAt) {
  if (!resetAt) return '';
  
  try {
    const date = new Date(resetAt);
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return resetAt; // Return original value for invalid dates
    }
    
    const now = new Date();
    
    // If reset is today, show time only
    if (date.toDateString() === now.toDateString()) {
      return date.toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    }
    
    // If reset is tomorrow, show "tomorrow at time"
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (date.toDateString() === tomorrow.toDateString()) {
      return `tomorrow at ${date.toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit' 
      })}`;
    }
    
    // Otherwise show full date and time
    return date.toLocaleString([], {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    console.warn('Error formatting reset time:', error);
    return resetAt;
  }
}

/**
 * Get a user-friendly quota message
 * @param {QuotaInfo} quota - Quota information
 * @returns {string} User-friendly message
 */
function getQuotaMessage(quota) {
  if (!quota) return 'Usage limit reached. Please go to usage page to see your current usage.';
  
  // Prioritize the detail message from API response (if available and meaningful)
  if (quota.message && quota.message !== 'Quota exceeded' && quota.message !== 'Plan Limit Exceeded') {
    return `${quota.message} Please go to usage page to see your current usage.`;
  }
  
  const scope = quota.scope || quota.type || 'usage';
  const resetTime = formatResetTime(quota.resetAt);
  
  let message = `You've reached your ${scope.toLowerCase()} limit.`;
  
  if (quota.used && quota.limit) {
    message += ` Used ${quota.used} of ${quota.limit}.`;
  }
  
  if (resetTime) {
    message += ` Resets ${resetTime}.`;
  } else {
    message += ' Upgrade your plan for more access.';
  }
  
  message += ' Please go to usage page to see your current usage.';
  
  return message;
}

export {
  QuotaExceededError,
  isQuotaError,
  normalizeQuotaPayload,
  formatResetTime,
  getQuotaMessage
};
